// GELÉ le 04/10/2026. Base saine = commit 338328aa.
// Interdit d'y retoucher sans un nouveau gel explicite de Hiti.
// Bugs déjà réglés, ne pas les rouvrir :
//
// 1. iPhone iOS 17.1+ (Safari et Chrome, même moteur) passe par hls.js dès que Hls.isSupported() est vrai. Pas de prefersNativeHls() pour choisir le lecteur. Pas de video.src sur un m3u8 dans ce cas. Le quatrième lecteur natif (load sans hls.js) faisait une carte noire : il reste interdit.
// 2. Voisines : hors économie de données, pas de stopLoad sur la carte visible. L'arrêt vers 3 s ne concerne que l'avance (N+2 et N-2) et l'économie de données. Les flux d'avance sont dans aheadStreams, pas dans slots. maxSources() reste 3.
// 3. iOS coupe le chargement hors écran (endstreaming). keepBuffering rappelle resumeBuffering tant que la carte n'est pas active avec le chargement ouvert. startPlayback rappelle resumeBuffering si bufferingEnabled est faux.
// 4. Ne pas passer de muet à sonore avant play(). Démute dans le then() de play(), une fois la lecture partie. Sinon iOS relance le décodeur : micro-pause.
// 5. Boucle : el.loop reste. En plus, wrapLoopBeforeEnd recadre currentTime à 0 s'il reste moins de 0,25 s ET que le début est dans le tampon. Une fois par tour. Pas de saut si le début n'est pas tamponné. C'est la seule écriture de currentTime hors du seek du membre.
// 6. Reprise : si le tampon ne couvre pas currentTime, startLoad(currentTime) avant play().
// 7. Palier admin (video_playback_start_quality) lu avant l'ouverture du fil. Hauteur fixe : testBandwidth false, capLevelToPlayerSize false, autoStartLoad false. Pas de nextLevel avant startLoad (startLoad remet le niveau à -1).
// 8. Vidéos verticales 9:16. Bunny : 360p = 360x640, 720p = 720x1280. Le palier se mesure avec Math.min(width, height), jamais la hauteur seule. Sinon 640 est plus près de 720 que 1280, et le départ reste en 360.
// 9. minAutoBitrate = débit du palier choisi. L'ABR peut monter, pas descendre en dessous. Pas de loadLevel ni currentLevel.
// 10. watchStuck ne détruit pas une source qui a hls.js. La bulle son lit le déverrouillage de session, pas muted.
// 12. Balise née muette (attribut muted=""). Reprises AbortError / setActive idempotent : play() simple si sonore, jamais d'écriture de muted.
// 11. Son déverrouillé : muted = false uniquement dans le geste (pointerdown ou clic), jamais dans startPlayback. Une carte neuve naît muette.
// GEL BÊTA 07/10 — docs/GEL_BETA.md. Porte ouverte aux retours Hiti, sans toucher la logique lecteur.
import Hls from "hls.js";
import { Video } from "@/types/videos";
import { subscribePlaybackUrl, isDataSaverMode } from "@/services/playbackUrls";
import { logVideoStartTime } from "@/services/videoMetrics";
import { resolveImmediateUrl, fallbackUrlOf } from "./sourceUrl";
import type { PlaybackState } from "./engineApi";
import { buildHlsConfig, bindStartQuality } from "./hlsConfig";
import { isAudioUnlocked, onAudioUnlocked } from "./audioSession";
import type { PlaybackEngineApi } from "./engineApi";
import { watchVideoFit } from "./videoFit";

/**
 * Moteur du lecteur « deck » (3 cartes permanentes).
 *
 * Deux principes, et tout le reste en découle :
 *
 * 1. L'identité d'un emplacement est son **élément `<video>`**, jamais l'id de
 *    la vidéo. Dans un deck, les cartes sont stables et les vidéos tournent au
 *    travers : indexer par vidéo créerait un état neuf à chaque rotation sur un
 *    nœud déjà enregistré, avec des écouteurs empilés à chaque geste.
 * 2. Le préchauffage des voisines est **borné** : hls.js charge, puis on coupe
 *    dès le premier fragment mis en tampon. La première image est décodée et
 *    prête à l'affichage, sans télécharger la vidéo entière.
 */

type Listener = (state: PlaybackState) => void;

interface Slot {
  el: HTMLVideoElement;
  hls: Hls | null;
  /** Vidéo actuellement portée par cette carte, `null` si la carte est vide. */
  videoId: string | null;
  index: number;
  url: string | null;
  unsubscribeUrl: (() => void) | null;
  activatedAt: number | null;
  startLogged: boolean;
  /** Chargement coupé après le premier fragment (voisine préchauffée). */
  warmPaused: boolean;
  /** Vidéo portée par la carte, conservée pour pouvoir reposer la source. */
  video: Video | null;
  /** Filet anti-blocage déjà utilisé pour cette vidéo (une seule tentative). */
  retried: boolean;
  /** Pose de source différée, en attente du vidage réel de l'élément. */
  pendingAttach: (() => void) | null;
  /** Compteur invalidant toute pose différée devenue obsolète. */
  generation: number;
  /** Pause demandée par le membre : jamais de relance automatique. */
  userPaused: boolean;
  /** Relance après un play() interrompu déjà utilisée (une seule). */
  abortRetried: boolean;
  /** Surveillance anti-pause iOS de la voisine (jusqu'à la première image). */
  stopKeepBuffering?: () => void;
  /** Écoute « première image » qui libérera la réserve de cette vidéo. */
  stopReleaseWatch?: () => void;
  /** Boucle déjà recadrée ce tour : pas de nouveau saut avant 1 s de lecture. */
  loopWrapped: boolean;
}

/**
 * Budget de flux attachés simultanément. Sur WebKit la lecture est native :
 * au-delà de deux flux, iOS cesse de décoder. Ailleurs (MSE), trois tiennent.
 * C'est le nombre de FLUX qui est plafonné, jamais le nombre de cartes.
 */
function maxSources(): number {
  // Trois cartes, trois flux : à deux, la voisine PRÉCÉDENTE n'obtenait jamais
  // de source, d'où un écran noir en remontant le feed.
  return 3;
}

/** Secondes de vidéo à mettre en tampon avant de couper le préchauffage. */
const WARM_BUFFER_SECONDS = 3;

const slots: Slot[] = [];
let activeSlot: Slot | null = null;
/** Vidéo qui vient d'être quittée : protégée pour un retour instantané. */
let lastPlayedId: string | null = null;
/**
 * Cartes créées après le déverrouillage du son : WebKit lève sa restriction
 * d'autoplay élément par élément, et seulement pendant un geste réel.
 */
const pendingUnlock = new Set<Slot>();

const listeners = new Map<string, Set<Listener>>();

/* ------------------------------------------------------------------ */
/* État diffusé à l'interface                                          */
/* ------------------------------------------------------------------ */

function slotOfVideo(videoId: string): Slot | undefined {
  return slots.find((slot) => slot.videoId === videoId);
}

function emit(videoId: string | null, state: Partial<PlaybackState>) {
  if (!videoId) return;
  const set = listeners.get(videoId);
  if (!set || set.size === 0) return;
  const el = slotOfVideo(videoId)?.el;
  const full: PlaybackState = {
    currentTime: el?.currentTime ?? 0,
    duration: Number.isFinite(el?.duration) ? (el?.duration as number) : 0,
    isPlaying: !!el && !el.paused,
    isWaiting: !slotOfVideo(videoId)?.url,
    error: null,
    autoplayBlocked: false,
    isMuted: el?.muted ?? true,
    ...state,
  };
  set.forEach((listener) => listener(full));
}

/* ------------------------------------------------------------------ */
/* Enregistrement d'une carte                                          */
/* ------------------------------------------------------------------ */

/** Retrouve l'emplacement d'un élément, ou l'enregistre au premier appel. */
function slotOfElement(el: HTMLVideoElement): Slot {
  const existing = slots.find((slot) => slot.el === el);
  if (existing) return existing;

  el.className = "absolute inset-0 w-full h-full object-cover";
  watchVideoFit(el);
  el.loop = true;
  el.playsInline = true;
  el.setAttribute("playsinline", "");
  el.setAttribute("webkit-playsinline", "true");
  el.setAttribute("x5-playsinline", "true");
  el.preload = "auto";
  el.defaultMuted = true;
  el.muted = true;
  el.setAttribute("muted", "");

  const slot: Slot = {
    el,
    hls: null,
    videoId: null,
    index: 0,
    url: null,
    unsubscribeUrl: null,
    activatedAt: null,
    userPaused: false,
    abortRetried: false,
    startLogged: false,
    warmPaused: false,
    video: null,
    retried: false,
    loopWrapped: false,
    pendingAttach: null,
    generation: 0,
  };

  // Carte apparue après le premier geste : elle n'a jamais reçu l'autorisation
  // WebKit, il faudra la lui donner au prochain geste utilisateur.
  if (isAudioUnlocked()) pendingUnlock.add(slot);

  el.addEventListener("timeupdate", () => {
    // Boucle sans pause sur iPhone (hls.js) : recadrer AVANT la fin quand le
    // début du tampon existe encore. Carte non active : on n'y touche pas.
    if (slot === activeSlot && slot.hls) wrapLoopBeforeEnd(slot);
    emit(slot.videoId, {});
  });
  el.addEventListener("loadedmetadata", () => emit(slot.videoId, {}));
  el.addEventListener("play", () => emit(slot.videoId, { isPlaying: true }));
  el.addEventListener("pause", () => emit(slot.videoId, { isPlaying: false }));
  el.addEventListener("playing", () => {
    if (slot === activeSlot && slot.activatedAt !== null && !slot.startLogged) {
      slot.startLogged = true;
      logVideoStartTime(performance.now() - slot.activatedAt, slot.videoId || "");
    }
    emit(slot.videoId, { isPlaying: true });
  });

  slots.push(slot);
  return slot;
}

/**
 * Pose le son DANS le geste (pointerdown / clic) : seul moment où WebKit
 * accepte un play() sonore. Une seule écriture muted = false par carte muette.
 * Voisine : play() puis pause() dans le then() (pas de pause synchrone, qui
 * annulerait l'autorisation), sans jamais laisser la carte active arrêtée.
 */
function poserLeSonDansLeGeste() {
  slots.forEach((slot) => {
    if (!slot.el.muted) return;
    slot.el.muted = false;
    emit(slot.videoId, {});
    if (slot === activeSlot) {
      if (slot.el.paused && !slot.userPaused) slot.el.play().catch(() => undefined);
      return;
    }
    if (!slot.url) return;
    slot.el
      .play()
      .then(() => {
        if (slot !== activeSlot) slot.el.pause();
        if (activeSlot && activeSlot.el.paused && !activeSlot.userPaused) {
          activeSlot.el.play().catch(() => undefined);
        }
      })
      .catch(() => undefined);
  });
  pendingUnlock.clear();
}

/** Premier geste de la session : ce callback tourne encore dans le clic. */
onAudioUnlocked(() => {
  poserLeSonDansLeGeste();
  if (activeSlot && activeSlot.el.paused && !activeSlot.userPaused) {
    activeSlot.el.play().catch(() => undefined);
  }
});

/* ------------------------------------------------------------------ */
/* Sources                                                             */
/* ------------------------------------------------------------------ */

function clearSource(slot: Slot) {
  slot.unsubscribeUrl?.();
  slot.unsubscribeUrl = null;
  slot.stopKeepBuffering?.();
  slot.stopKeepBuffering = undefined;
  slot.stopReleaseWatch?.();
  if (slot.hls) {
    slot.hls.destroy();
    slot.hls = null;
  }
  /*
   * Réinitialisation INCONDITIONNELLE du décodeur : une source posée par hls.js
   * ne passe pas par l'attribut `src`, et sans ce `load()` l'élément recyclé
   * gardait l'état de la vidéo précédente (couleurs négatives sur Android).
   */
  slot.el.removeAttribute("src");
  slot.el.load();
  slot.url = null;
  slot.warmPaused = false;
  slot.loopWrapped = false;
  // Toute pose de source encore en attente devient obsolète.
  slot.generation += 1;
  slot.pendingAttach = null;
}

/**
 * Une vidéo hors écran n'a pas forcément d'image décodée, même en
 * `preload="auto"`. Un aller-retour play()/pause() muet force le décodage de la
 * première image : la carte voisine a donc une image, et non un rectangle noir.
 * La pause n'intervient qu'une fois l'image là ; sinon la lecture muette
 * continue jusqu'à la première image. Un play() refusé : un seul nouvel essai
 * sur `loadeddata` ou `canplay`, sans boucle.
 */
function primeFirstFrame(slot: Slot, retry = true) {
  // Lot du 07/10/2026 : aucune écriture de muted ici (une carte naît muette).
  const generation = slot.generation;
  const stale = () => slot.generation !== generation || slot === activeSlot;

  const retryOnce = () => {
    if (!retry) return;
    let done = false;
    const fire = () => {
      if (done) return;
      done = true;
      slot.el.removeEventListener("loadeddata", fire);
      slot.el.removeEventListener("canplay", fire);
      if (stale()) return;
      primeFirstFrame(slot, false);
    };
    slot.el.addEventListener("loadeddata", fire, { once: true });
    slot.el.addEventListener("canplay", fire, { once: true });
  };

  // Si la carte est devenue active pendant l'amorçage, la mettre en pause
  // figerait la vidéo que l'utilisateur vient justement de demander.
  const pauseWhenReady = () => {
    if (slot === activeSlot) return;
    if (slot.el.readyState >= 2) {
      slot.el.pause();
      return;
    }
    slot.el.addEventListener(
      "loadeddata",
      () => {
        if (stale()) return;
        slot.el.pause();
      },
      { once: true },
    );
  };

  // Lot du 07/10/2026 : son déverrouillé et élément sonore → pas d'amorce par
  // lecture, une voisine hors écran ne doit jamais jouer de son.
  if (isAudioUnlocked() && !slot.el.muted) return;
  const attempt = slot.el.play();
  if (attempt && typeof attempt.then === "function") {
    attempt.then(pauseWhenReady).catch(retryOnce);
  } else {
    pauseWhenReady();
  }
}

/* ------------------------------------------------------------------ */
/* Préchargement à deux crans (hors deck)                              */
/* ------------------------------------------------------------------ */

/**
 * Flux d'avance N+2 / N-2. Tableau À PART : jamais dans `slots`, sinon ils
 * compteraient dans `maxSources()` et chasseraient la vidéo précédente.
 */
interface AheadStream {
  el: HTMLVideoElement;
  hls: Hls | null;
  videoId: string;
  /** Abonnement à l'adresse signée, annulé à la libération du flux. */
  unsubscribe?: () => void;
  /** Surveillance anti-pause iOS, arrêtée au stopLoad ou à la libération. */
  stopKeepBuffering?: () => void;
}

const aheadStreams: AheadStream[] = [];
/*
 * Abonnements posés en attendant l'adresse signée d'une vidéo à deux crans.
 * Liste À PART : ils n'ont pas encore d'AheadStream. Annulés dans reset() et
 * quand la vidéo n'est plus une cible de l'appel le plus récent.
 */
const pendingAheadSubscriptions = new Map<string, () => void>();
/** Cibles de l'appel warmDeckAhead le plus récent. */
let lastWantedAheadIds = new Set<string>();
/** Sens du dernier geste accepté (1 = vers le bas). */
let lastDirection: 1 | -1 = 1;

type DeckItem = Video | { id: string; isAd: true };

/** Retient le sens du dernier geste, pour l'avance iPhone. */
export function noteDeckDirection(step: 1 | -1) {
  lastDirection = step;
}

/**
 * Élément placé DANS le document mais hors écran : un élément seulement en
 * mémoire ne télécharge rien, surtout sur iPhone.
 */
function createAheadElement(): HTMLVideoElement {
  const el = document.createElement("video");
  el.defaultMuted = true;
  el.muted = true;
  el.setAttribute("muted", "");
  el.playsInline = true;
  el.setAttribute("playsinline", "");
  el.setAttribute("webkit-playsinline", "true");
  el.setAttribute("aria-hidden", "true");
  el.preload = "auto";
  /*
   * Taille de la fenêtre, pas 1 px : Safari choisit le palier HLS d'après la
   * taille de l'élément. À 1 px, la vidéo à deux crans était demandée au plus
   * petit palier (image minuscule floue au deuxième glissement). hls.js ne
   * tient pas compte de cette taille (capLevelToPlayerSize est faux pour une
   * hauteur fixe), PC et Android ne changent donc pas de comportement.
   */
  Object.assign(el.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: `${window.innerWidth}px`,
    height: `${window.innerHeight}px`,
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.appendChild(el);
  return el;
}

function releaseAhead(stream: AheadStream) {
  stream.unsubscribe?.();
  stream.unsubscribe = undefined;
  stream.stopKeepBuffering?.();
  stream.stopKeepBuffering = undefined;
  stream.hls?.destroy();
  stream.hls = null;
  stream.el.removeAttribute("src");
  stream.el.load();
  stream.el.remove();
  const position = aheadStreams.indexOf(stream);
  if (position >= 0) aheadStreams.splice(position, 1);
}

/** Une carte visible prend cette vidéo : le flux d'avance n'a plus lieu d'être. */
function releaseAheadOf(videoId: string) {
  aheadStreams.filter((stream) => stream.videoId === videoId).forEach(releaseAhead);
}

/**
 * Libère la réserve d'une vidéo seulement quand la carte qui la porte a sa
 * première image (loadeddata, une fois). Si la carte change de vidéo avant,
 * rien n'est libéré.
 */
function releaseAheadWhenShown(slot: Slot, videoId: string) {
  slot.stopReleaseWatch?.();
  const generation = slot.generation;
  const onLoaded = () => {
    slot.stopReleaseWatch = undefined;
    if (slot.videoId !== videoId || slot.generation < generation) return;
    releaseAheadOf(videoId);
  };
  slot.el.addEventListener("loadeddata", onLoaded, { once: true });
  slot.stopReleaseWatch = () => {
    slot.el.removeEventListener("loadeddata", onLoaded);
    slot.stopReleaseWatch = undefined;
  };
}

/** Intervalle de la surveillance anti-pause iOS. */
const KEEP_BUFFERING_MS = 250;

/**
 * iOS (ManagedMediaSource) met le chargement en pause hors écran. Tant que
 * `done()` est faux, on le relance. S'arrête seul dès que `done()` est vrai ;
 * renvoie la fonction d'annulation.
 */
function keepBuffering(hls: Hls, done: () => boolean): () => void {
  const timer = window.setInterval(() => {
    if (done()) {
      window.clearInterval(timer);
      return;
    }
    if (!hls.bufferingEnabled) hls.resumeBuffering();
  }, KEEP_BUFFERING_MS);
  return () => window.clearInterval(timer);
}

function startAhead(video: Video, url: string) {
  const el = createAheadElement();
  const stream: AheadStream = { el, hls: null, videoId: video.id };
  const isHls = url.includes(".m3u8");

  if (isHls && Hls.isSupported()) {
    const hls = new Hls(buildHlsConfig());
    bindStartQuality(hls);
    hls.loadSource(url);
    hls.attachMedia(el);
    let reached = false;
    const onFrag = () => {
      const buffered = el.buffered;
      const ahead = buffered.length > 0 ? buffered.end(0) : 0;
      if (ahead < WARM_BUFFER_SECONDS) return;
      hls.off(Hls.Events.FRAG_BUFFERED, onFrag);
      // Même tour : objectif atteint, minuteur arrêté, PUIS stopLoad. Aucun
      // tick ne peut relancer le chargement après.
      reached = true;
      stream.stopKeepBuffering?.();
      stream.stopKeepBuffering = undefined;
      hls.stopLoad();
    };
    hls.on(Hls.Events.FRAG_BUFFERED, onFrag);
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data.fatal) releaseAhead(stream);
    });
    stream.hls = hls;
    stream.stopKeepBuffering = keepBuffering(hls, () => reached || stream.hls !== hls);
  } else {
    // iPhone et fichiers simples : préchargement natif, SANS play().
    el.src = url;
    el.load();
  }
  aheadStreams.push(stream);
}

/**
 * Prépare la vidéo à deux crans, N+2 et N-2, partout où hls.js est supporté
 * (PC, Android, iPhone iOS 17.1+). Rien en économie de données.
 */
function clearAhead() {
  [...aheadStreams].forEach(releaseAhead);
  pendingAheadSubscriptions.forEach((unsubscribe) => unsubscribe());
  pendingAheadSubscriptions.clear();
  lastWantedAheadIds = new Set();
}

export function warmDeckAhead(videos: DeckItem[], currentIndex: number) {
  // Sans hls.js (vieux iOS) : aucun lecteur caché, trois cartes natives.
  // Un 4e load() natif ferait cesser le décodage iOS.
  if (isDataSaverMode() || !Hls.isSupported()) {
    clearAhead();
    return;
  }

  // Pour chaque sens : ±2, ou ±3 si ±2 est une publicité ou absent. Jamais plus loin.
  const wanted: Video[] = [];
  [1, -1].forEach((direction) => {
    for (const distance of [2, 3]) {
      const index = currentIndex + direction * distance;
      if (index < 0 || index >= videos.length) continue;
      const item = videos[index];
      if (!item || "isAd" in item) continue;
      wanted.push(item as Video);
      return;
    }
  });

  const wantedIds = new Set(wanted.map((video) => video.id));
  lastWantedAheadIds = wantedIds;
  [...aheadStreams]
    .filter((stream) => !wantedIds.has(stream.videoId))
    .forEach(releaseAhead);
  // Une vidéo qui n'est plus une cible n'a plus à attendre son adresse.
  pendingAheadSubscriptions.forEach((unsubscribe, videoId) => {
    if (wantedIds.has(videoId)) return;
    unsubscribe();
    pendingAheadSubscriptions.delete(videoId);
  });

  wanted.forEach((video) => {
    if (aheadStreams.some((stream) => stream.videoId === video.id)) return;
    if (slots.some((slot) => slot.videoId === video.id && slot.url)) return;
    const url = resolveImmediateUrl(video);
    if (url) {
      startAhead(video, url);
      return;
    }
    /*
     * Adresse signée pas encore là : on s'abonne. Au arrivée, on ne lance
     * que si la vidéo est toujours une cible de l'appel le plus récent.
     */
    if (pendingAheadSubscriptions.has(video.id)) return;
    const unsubscribe = subscribePlaybackUrl(video.id, (freshUrl) => {
      pendingAheadSubscriptions.delete(video.id);
      if (!lastWantedAheadIds.has(video.id)) return;
      if (aheadStreams.some((stream) => stream.videoId === video.id)) return;
      if (slots.some((slot) => slot.videoId === video.id && slot.url)) return;
      startAhead(video, freshUrl);
    });
    pendingAheadSubscriptions.set(video.id, unsubscribe);
  });
}

/**
 * `load()` rend la main AVANT que l'élément soit réellement vidé. Reposer
 * aussitôt une source (surtout une MediaSource hls.js) laisse Android composer
 * les premières images avec la conversion couleur du flux précédent : c'est le
 * filtre « négatif ». On attend donc l'évènement `emptied`, avec un minuteur de
 * secours car un élément encore neuf ne l'émet jamais.
 */
function attachWhenEmptied(slot: Slot, run: () => void) {
  const generation = slot.generation;
  let timer: number | null = null;

  const fire = () => {
    slot.el.removeEventListener("emptied", fire);
    if (timer !== null) window.clearTimeout(timer);
    if (slot.generation !== generation) return;
    slot.pendingAttach = null;
    run();
  };

  slot.pendingAttach = fire;
  slot.el.addEventListener("emptied", fire, { once: true });
  timer = window.setTimeout(fire, 60);
}

/** Exécute sans attendre une pose de source différée. */
function flushPendingAttach(slot: Slot) {
  slot.pendingAttach?.();
}

/** Pose réellement la source sur l'élément. */
function attachSource(slot: Slot, video: Video, url: string, warm: boolean) {
  const isHls = url.includes(".m3u8");
  const fallback = fallbackUrlOf(video);

  if (isHls && Hls.isSupported()) {
    if (slot.el.getAttribute("src")) {
      slot.el.removeAttribute("src");
      slot.el.load();
    }
    const hls = new Hls(buildHlsConfig());
    bindStartQuality(hls);
    hls.loadSource(url);
    hls.attachMedia(slot.el);
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (data.fatal && fallback && fallback !== url) {
        hls.destroy();
        slot.hls = null;
        slot.url = fallback;
        slot.el.src = fallback;
      }
    });
    // Hors économie de données, la voisine charge jusqu'au plafond de hls.js,
    // sans coupure : au geste, play() tombe sur un tampon déjà rempli.
    if (warm && isDataSaverMode()) {
      /*
       * Préchauffage borné à ~3 secondes en tampon, pas à un seul fragment :
       * avec un fragment, la lecture démarrait puis attendait aussitôt le
       * suivant, ce qui donnait une pause de moins d'une seconde au départ.
       */
      const onFrag = () => {
        if (slot === activeSlot) return;
        const buffered = slot.el.buffered;
        const ahead = buffered.length > 0 ? buffered.end(0) : 0;
        if (ahead < WARM_BUFFER_SECONDS) return;
        hls.off(Hls.Events.FRAG_BUFFERED, onFrag);
        hls.stopLoad();
        slot.warmPaused = true;
      };
      hls.on(Hls.Events.FRAG_BUFFERED, onFrag);
    }
    slot.hls = hls;
    if (warm) {
      // iOS met en pause le chargement hors écran : on le relance jusqu'à ce
      // que la carte soit active avec un chargement ouvert.
      slot.stopKeepBuffering?.();
      slot.stopKeepBuffering = keepBuffering(
        hls,
        () => slot.hls !== hls || (slot === activeSlot && hls.bufferingEnabled),
      );
    }
  } else if (isHls && !slot.el.canPlayType("application/vnd.apple.mpegurl") && fallback) {
    slot.el.src = fallback;
    slot.url = fallback;
  } else {
    // Lecture native : le navigateur décide seul de son tampon.
    slot.el.src = url;
    slot.el.preload = "auto";
    slot.el.load();
  }

  if (warm) primeFirstFrame(slot);
}

/**
 * Attache une source à la carte.
 *
 * `warm` : voisine préparée mais pas jouée. hls.js charge normalement puis on
 * coupe le chargement au premier fragment mis en tampon ; la reprise a lieu à
 * l'activation. Une voisine ne consomme donc qu'un fragment.
 */
function attach(slot: Slot, video: Video, url: string, warm: boolean) {
  if (slot.url === url) return;

  /*
   * Une URL signée peut arriver APRÈS que la carte soit déjà lisible (résolution
   * en lot). Remplacer la source détruirait l'instance et la vidéo repartirait
   * de zéro : on ignore la nouvelle URL dans ce cas.
   */
  if (slot.url && slot.el.readyState >= 2) return;

  if (slot.hls) {
    slot.stopKeepBuffering?.();
    slot.stopKeepBuffering = undefined;
    slot.hls.destroy();
    slot.hls = null;
  }
  slot.url = url;
  slot.startLogged = false;
  slot.warmPaused = false;
  slot.generation += 1;
  slot.pendingAttach = null;

  if (warm) {
    attachWhenEmptied(slot, () => attachSource(slot, video, url, true));
  } else {
    // La carte active ne doit JAMAIS attendre.
    attachSource(slot, video, url, false);
  }

  emit(slot.videoId, { isWaiting: false });
}

/** Pose une vidéo sur une carte. Ne touche à rien si la carte la porte déjà. */
function load(slot: Slot, video: Video, warm: boolean) {
  if (slot.videoId === video.id && slot.url) return;

  clearSource(slot);
  slot.videoId = video.id;
  slot.video = video;
  // La réserve n'est libérée qu'une fois l'image de cette carte présente.
  releaseAheadWhenShown(slot, video.id);
  slot.retried = false;
  slot.activatedAt = null;
  slot.startLogged = false;
  slot.el.removeAttribute("poster");
  // Lot du 06/10/2026 : jamais muted = false ici ; une seule écriture si besoin.
  if (!isAudioUnlocked() && !slot.el.muted) slot.el.muted = true;

  const url = resolveImmediateUrl(video);
  if (url) attach(slot, video, url, warm);
  else emit(slot.videoId, { isWaiting: true });

  slot.unsubscribeUrl = subscribePlaybackUrl(video.id, (fresh) => {
    if (slot.videoId !== video.id) return;
    attach(slot, video, fresh, slot !== activeSlot);
    if (slot === activeSlot && !slot.userPaused) startPlayback(slot);
  });
}

/** Lecture immédiate, sans `await` : compatible avec le geste utilisateur iOS. */

/** Reste de durée en dessous duquel on recadre la tête au début. */
const LOOP_WRAP_REMAINING_S = 0.25;
/** Recadrage autorisé seulement si le tampon commence ici ou avant. */
const LOOP_WRAP_BUFFER_START_S = 0.05;

/**
 * Boucle sans pause sur iPhone (hls.js) : à l'approche de la fin, Safari jette
 * le début du tampon et recharge le premier morceau — pause visible. Si le
 * début est encore en tampon, on recadre la tête nous-mêmes avant la fin.
 * Un seul recadrage par tour : le suivant n'est permis qu'après avoir repassé
 * 1 s de lecture. Début absent du tampon : on laisse la boucle du navigateur,
 * un saut à vide creuserait le trou qu'on veut supprimer.
 */
function wrapLoopBeforeEnd(slot: Slot) {
  const el = slot.el;
  const duration = el.duration;
  if (!Number.isFinite(duration) || duration <= 0) return;
  if (duration - el.currentTime >= LOOP_WRAP_REMAINING_S) {
    // Tour reparti : on réarme pour la prochaine fin.
    if (el.currentTime > 1) slot.loopWrapped = false;
    return;
  }
  if (slot.loopWrapped) return;
  const buffered = el.buffered;
  if (buffered.length === 0) return;
  if (buffered.start(0) > LOOP_WRAP_BUFFER_START_S) return;
  slot.loopWrapped = true;
  // Le début doit être demandé AVANT le saut, sinon iPhone reste en pause à 0.
  if (slot.hls && !bufferCoversTime(el, 0)) slot.hls.startLoad(0);
  el.currentTime = 0;
}

/** Vrai si un intervalle du tampon contient la position donnée. */
function bufferCoversTime(el: HTMLVideoElement, time: number): boolean {
  const buffered = el.buffered;
  for (let i = 0; i < buffered.length; i += 1) {
    if (time >= buffered.start(i) && time <= buffered.end(i)) return true;
  }
  return false;
}

function startPlayback(slot: Slot) {
  // Une source différée doit être posée tout de suite : la carte devient active.
  flushPendingAttach(slot);
  if (!slot.url) return;
  if (slot.hls && slot.warmPaused) {
    slot.hls.startLoad();
    slot.warmPaused = false;
  }
  // iOS met le chargement hors écran en pause : on le rouvre avant play().
  if (slot.hls && !slot.hls.bufferingEnabled) slot.hls.resumeBuffering();
  /*
   * Reprise sans pause sur iPhone : si le tampon ne couvre plus la tête de
   * lecture, faire partir le morceau AVANT play(), pas après la pause.
   */
  if (slot.hls && !bufferCoversTime(slot.el, slot.el.currentTime)) {
    slot.hls.startLoad(slot.el.currentTime);
  }
  slot.el.loop = true;
  // play() garde l'état de muted déjà posé : le son se pose uniquement dans le
  // geste (poserLeSonDansLeGeste). Avant le premier geste : départ muet.
  if (!isAudioUnlocked() && !slot.el.muted) slot.el.muted = true;
  const attempt = slot.el.play();
  if (attempt && typeof attempt.catch === "function") {
    attempt.then(() => {
      slot.abortRetried = false;
    }).catch((error: unknown) => {
      // play() interrompu (pause, changement de source) : ce n'est pas un refus
      // d'autoplay. On garde le son et on relance une seule fois.
      if ((error as { name?: string } | null)?.name === "AbortError") {
        if (slot.abortRetried) return;
        slot.abortRetried = true;
        window.requestAnimationFrame(() => {
          if (slot !== activeSlot || !slot.el.paused || slot.userPaused) return;
          if (slot.el.muted) {
            startPlayback(slot);
            return;
          }
          // Carte sonore : simple reprise, sans jamais toucher à muted.
          slot.el.play().catch(() => undefined);
        });
        return;
      }
      // Toujours repartir en muet : mieux vaut l'image sans le son qu'une carte
      // figée, même quand le son a déjà été débloqué ailleurs.
      slot.el.muted = true;
      slot.el.play().catch(() => undefined);
    });
  }
}

/**
 * Filet anti-blocage (Safari iOS) : si une seconde après l'activation la lecture
 * n'a pas avancé alors que l'élément n'est pas en pause, le décodeur n'a jamais
 * démarré. On repose la source et on retente UNE SEULE fois : une vidéo
 * réellement illisible ne doit pas boucler indéfiniment.
 */
function watchStuck(slot: Slot, video: Video, attempt = 0) {
  window.setTimeout(() => {
    if (slot !== activeSlot || slot.videoId !== video.id) return;
    if (slot.retried) return;
    if (slot.el.currentTime > 0 || slot.el.paused) return;
    // hls.js gère ses propres erreurs et bascule sur le MP4 de repli.
    if (slot.hls) return;
    // Adresse posée mais pas encore d'image : on revérifie (3 fois au plus),
    // quel que soit l'état du téléchargement. Rien n'est détruit d'ici là.
    if (attempt < 3 && slot.el.readyState < 2 && slot.el.getAttribute("src")) {
      watchStuck(slot, video, attempt + 1);
      return;
    }

    slot.retried = true;
    clearSource(slot);
    slot.videoId = video.id;
    slot.video = video;
    const url = resolveImmediateUrl(video);
    if (url) attach(slot, video, url, false);
    startPlayback(slot);
  }, 1000);
}

/**
 * Libère une source si le budget est atteint. La carte active et celle qu'on
 * vient de quitter sont protégées ; parmi les autres, la plus éloignée de
 * l'index courant perd sa source. Son élément reste en place.
 */
function makeSourceRoom(target: Slot, currentIndex: number) {
  const sourced = slots.filter((slot) => slot !== target && slot.url);
  if (sourced.length < maxSources()) return;

  const byDistance = (left: Slot, right: Slot) =>
    Math.abs(right.index - currentIndex) - Math.abs(left.index - currentIndex);

  const candidates = sourced
    .filter((slot) => slot !== activeSlot && slot.videoId !== lastPlayedId)
    .sort(byDistance);
  const fallback = sourced.filter((slot) => slot !== activeSlot).sort(byDistance);

  const toRelease = candidates[0] || fallback[0];
  if (toRelease) {
    toRelease.el.pause();
    clearSource(toRelease);
  }
}

/* ------------------------------------------------------------------ */
/* Voisines                                                            */
/* ------------------------------------------------------------------ */

/**
 * Prépare une carte voisine sans la jouer.
 *
 * `mayEvict` est vrai pour la voisine du sens du geste : elle mérite qu'on
 * libère une source ailleurs. Il est faux pour l'autre, sinon les deux
 * voisines s'évinceraient l'une l'autre à chaque geste et aucune ne serait
 * jamais prête.
 */
export function prepareDeckNeighbour(
  video: Video,
  el: HTMLVideoElement | null,
  index: number,
  mayEvict: boolean,
) {
  if (!el) return;
  const slot = slotOfElement(el);
  slot.index = index;
  if (slot === activeSlot) return;
  if (slot.videoId === video.id && slot.url) return;

  const sourced = slots.filter((candidate) => candidate !== slot && candidate.url).length;
  if (sourced >= maxSources()) {
    if (!mayEvict) return;
    makeSourceRoom(slot, index);
  }

  load(slot, video, true);
}

/** Connexion économe : une seule voisine préparée, celle du sens du geste. */
export function shouldWarmBothNeighbours(): boolean {
  return !isDataSaverMode();
}

/**
 * Autorise la lecture des cartes créées après le déverrouillage du son.
 * À appeler DANS un geste utilisateur : WebKit n'accorde son autorisation
 * qu'élément par élément, et seulement à ce moment-là. Réécrire la même valeur
 * de `muted` serait ignoré, d'où l'aller-retour vrai → faux.
 */
export function unlockDeckElements() {
  // Dans le geste : les cartes visibles sans image lancent leur lecture muette.
  slots.forEach((slot) => {
    if (slot === activeSlot || !slot.url || slot.el.readyState >= 2) return;
    primeFirstFrame(slot);
  });
  if (!isAudioUnlocked()) return;
  poserLeSonDansLeGeste();
}

/** Met tout le deck en pause (publicité, sortie du feed) : plus aucun son. */
export function pauseDeck() {
  slots.forEach((slot) => slot.el.pause());
  if (activeSlot) lastPlayedId = activeSlot.videoId;
  activeSlot = null;
}

/* ------------------------------------------------------------------ */
/* API publique                                                        */
/* ------------------------------------------------------------------ */

export const deckEngine: PlaybackEngineApi = {
  setActive(video, el, index) {
    if (!el) return;
    const slot = slotOfElement(el);
    slot.index = index;

    /*
     * Idempotence : l'effet React repasse par ici après le recalage des cartes.
     * Sans cette sortie, la vidéo lancée dans le geste repartirait de zéro.
     */
    if (activeSlot === slot && slot.videoId === video.id && slot.url) {
      // Reprise si arrêtée, sans toucher à muted (le son ne se pose que dans le geste).
      if (slot.el.paused && !slot.userPaused) {
        if (slot.el.muted) startPlayback(slot);
        else slot.el.play().catch(() => undefined);
      }
      return;
    }

    if (!slot.url || slot.videoId !== video.id) makeSourceRoom(slot, index);
    load(slot, video, false);

    if (activeSlot && activeSlot !== slot) {
      if (!activeSlot.el.paused) activeSlot.el.pause();
      lastPlayedId = activeSlot.videoId;
    }
    activeSlot = slot;
    slot.userPaused = false;
    slot.abortRetried = false;
    slot.activatedAt = performance.now();
    slot.startLogged = false;
    startPlayback(slot);
    watchStuck(slot, video);
  },

  prefetch(video, el, index) {
    prepareDeckNeighbour(video, el, index, false);
  },

  detach(videoId) {
    const slot = slotOfVideo(videoId);
    if (!slot) return;
    slot.el.pause();
    if (slot === activeSlot) {
      lastPlayedId = slot.videoId;
      activeSlot = null;
    }
    clearSource(slot);
    slot.videoId = null;

    /*
     * Carte réellement retirée du document : sans cette purge, elle resterait
     * comptée dans le budget de flux et `slotOfVideo` pourrait rendre un
     * élément qui n'est plus à l'écran.
     */
    if (!slot.el.isConnected) {
      pendingUnlock.delete(slot);
      const position = slots.indexOf(slot);
      if (position >= 0) slots.splice(position, 1);
    }
  },

  play(videoId) {
    const slot = videoId ? slotOfVideo(videoId) : activeSlot;
    if (!slot) return;
    slot.userPaused = false;
    startPlayback(slot);
  },

  pause(videoId) {
    const slot = videoId ? slotOfVideo(videoId) : activeSlot;
    if (!slot) return;
    slot.userPaused = true;
    slot.el.pause();
  },

  isPlaying(videoId) {
    const slot = slotOfVideo(videoId);
    return !!slot && !slot.el.paused;
  },

  seek(videoId, time) {
    const slot = slotOfVideo(videoId);
    if (slot) slot.el.currentTime = time;
  },

  getElement(videoId) {
    return slotOfVideo(videoId)?.el || null;
  },

  subscribe(videoId, listener) {
    let set = listeners.get(videoId);
    if (!set) {
      set = new Set();
      listeners.set(videoId, set);
    }
    set.add(listener);
    return () => {
      set?.delete(listener);
      if (set && set.size === 0) listeners.delete(videoId);
    };
  },

  reset() {
    slots.forEach((slot) => {
      slot.el.pause();
      clearSource(slot);
      slot.videoId = null;
    });
    slots.length = 0;
    [...aheadStreams].forEach(releaseAhead);
    pendingAheadSubscriptions.forEach((unsubscribe) => unsubscribe());
    pendingAheadSubscriptions.clear();
    lastWantedAheadIds = new Set();
    pendingUnlock.clear();
    activeSlot = null;
    lastPlayedId = null;
  },
};
